import { useState, useCallback, useEffect } from 'react';
import { ClipboardCheck, AlertCircle } from 'lucide-react';
import { StepIndicator } from './components/StepIndicator';
import { MonthStoreSelector } from './components/MonthStoreSelector';
import { StepSync } from './components/StepSync';
import { StepMissing } from './components/StepMissing';
import { StepNumbering } from './components/StepNumbering';
import { StepVerification } from './components/StepVerification';
import { RepairLog } from './components/RepairLog';
import { useReconciliation } from './useReconciliation';
import { reconciliationApi } from './reconciliation-api';
import api from '@/lib/api';

export function ReconciliationPage() {
  const {
    dateFrom, dateTo, storeId, monthLabel,
    activeStep, steps, isLoading, error,
    repairLog,
    setDateRange, setStoreId, setActiveStep,
    setDiagnostic, setLoading, setError,
  } = useReconciliation();

  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Load stores
  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data?.stores || r.data || [])).catch(() => {});
  }, []);

  const runDiagnostic = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reconciliationApi.fullDiagnostic(dateFrom, dateTo, storeId);
      setDiagnostic(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Błąd diagnostyki');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, storeId]);

  const handleDownloadSnapshot = useCallback(async () => {
    setIsDownloading(true);
    try {
      const data = await reconciliationApi.snapshot(dateFrom, dateTo, storeId);
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `snapshot-${dateFrom}-${dateTo}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Błąd pobierania snapshotu');
    } finally {
      setIsDownloading(false);
    }
  }, [dateFrom, dateTo, storeId]);

  const renderStep = () => {
    switch (activeStep) {
      case 'sync': return <StepSync />;
      case 'missing': return <StepMissing />;
      case 'numbering': return <StepNumbering />;
      case 'verification': return <StepVerification />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-text-primary flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-accent-primary" />
          Uzgadnianie faktur — {monthLabel}
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Krok po kroku do zamknięcia miesiąca księgowego
        </p>
      </div>

      {/* Month/store selector */}
      <MonthStoreSelector
        dateFrom={dateFrom}
        dateTo={dateTo}
        storeId={storeId}
        stores={stores}
        onDateChange={setDateRange}
        onStoreChange={setStoreId}
        onRefresh={runDiagnostic}
        onDownloadSnapshot={handleDownloadSnapshot}
        isLoading={isLoading}
        isDownloading={isDownloading}
      />

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/20 text-sm text-danger">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Step indicator */}
      <StepIndicator steps={steps} activeStep={activeStep} onStepClick={setActiveStep} />

      {/* Active step */}
      <div className="min-h-[300px]">
        {renderStep()}
      </div>

      {/* Repair log */}
      <RepairLog entries={repairLog} />
    </div>
  );
}
